import { Fragment } from 'react';

import type * as api from '@gitbook/api-client';
import {
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    Heading,
    Tooltip,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    TemplateCardActions,
    TemplateCardButton,
    TemplateCardFooter,
    TemplateCardImage,
    TemplateCardPreview,
} from './TemplateCard';
import { TemplatePreviewDialog } from './TemplatePreview';

export interface SpaceTemplateCardProps {
    /**
     * The organization ID to use for the template.
     */
    organizationId: string;

    /**
     * Template to display.
     */
    template: api.SpaceTemplate;

    /**
     * Callback when the template is applied.
     */
    onApplyTemplate: (template: api.SpaceTemplate) => Promise<void>;
    disabled?: boolean;
    disabledReason?: string;
    compact?: boolean;
}

/**
 * Card to display a space template.
 */
export function SpaceTemplateCard(props: SpaceTemplateCardProps) {
    const { template, onApplyTemplate, organizationId, disabled, disabledReason, compact } = props;
    const previewDialogTrigger = useDialogTriggerState();
    const card = (
        <TemplateCardButton key={template.id} disabled={disabled}>
            <TemplateCardPreview>
                <TemplateCardImage
                    className={compact ? 'aspect-auto! h-24!' : undefined}
                    src={{
                        light: template.previewImage.light,
                        dark: template.previewImage.dark,
                    }}
                />
                <TemplateCardActions>
                    <Button
                        label="Use template"
                        disabled={disabled}
                        onPress={() => onApplyTemplate(template)}
                    />
                    {disabled ? (
                        <Button label="Preview" kind="secondary" disabled />
                    ) : (
                        <DialogTrigger trigger={previewDialogTrigger}>
                            <Button label="Preview" kind="secondary" />
                        </DialogTrigger>
                    )}
                </TemplateCardActions>
            </TemplateCardPreview>
            <TemplateCardFooter className={compact ? 'p-3!' : undefined}>
                <div className="flex flex-col gap-1">
                    <Heading className={compact ? 'text-ui-heading-small' : undefined}>
                        {template.title}
                    </Heading>
                    <Description>{template.subtitle}</Description>
                </div>
            </TemplateCardFooter>
        </TemplateCardButton>
    );

    if (disabled) {
        return disabledReason ? <Tooltip tooltip={disabledReason}>{card}</Tooltip> : card;
    }

    return (
        <Fragment key={template.id}>
            <DialogTrigger trigger={previewDialogTrigger}>{card}</DialogTrigger>
            <DialogRoot {...previewDialogTrigger.rootProps}>
                <TemplatePreviewDialog
                    template={template}
                    onApplyTemplate={async () => {
                        previewDialogTrigger.close();
                        await onApplyTemplate(template);
                    }}
                    organizationId={organizationId}
                />
            </DialogRoot>
        </Fragment>
    );
}
